<template>
	<div style="height:100%" class="flex flex_between flex_column">
		<h4 class="font_lg font_bold margin_top">{{ $t("seller.upload.InvoiceDetails") }}</h4>

		<form class="form-horizontal flex_1" style="width:100%" action>
			<div class="md-layout">
				<div class="md-layout-item flex flex_column flex_between">
					<div style="width:100%">
						<AvailableDebtorSelect
							:value="debtorId"
							:api="loadAvailableDebtors"
							v-on:input="changeDebtor"
							v-on:redirect="beforeRedirect"
							:required="true"
							data-vv-name="debtorData"
							v-validate="modelValidations.debtorData"
							:error="errors.first('debtorData')"
						></AvailableDebtorSelect>
						<DebtorContactSelect
							:value="contactData"
							v-on:input="changeContact"
							:required="true"
							data-vv-name="contactData"
							v-validate="modelValidations.contactData"
							:error="errors.first('contactData')"
						></DebtorContactSelect>
					</div>
					<div class="flex flex_column" style="width:100%; margin-bottom: 16px">
						<md-checkbox v-model="form.requestObligorConfirmation">
							{{ $t("seller.upload.Request") }}
							<el-tooltip :content="$t('seller.upload.ipuRequest.tooltip')" placement="top">
								<i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
							</el-tooltip>
						</md-checkbox>
					</div>
					<div style="width:100%">
						<TypeSelect :typeId="form.type" v-on:onSelect="selectType"></TypeSelect>
						<md-field :class="[{ 'md-error': errors.has('invoiceNumber') }]">
							<label>{{ $t("seller.upload.InvoiceNumber") }}</label>
							<md-input v-model="form.invoiceNumber"
									  required
									  data-vv-name="invoiceNumber"
									  v-validate="modelValidations.invoiceNumber"
							></md-input>
							<span v-if="errors.has('invoiceNumber')"
								  class="validation-error">{{ errors.first("invoiceNumber") }}</span>
						</md-field>
						<div class="flex">
							<md-field :class="[{ 'md-error': errors.has('amount') }]">
								<label>{{ $t("seller.upload.Amount") }}</label>
								<MoneyAmountInput v-model="form.amount"
												  :required="true"
												  data-vv-name="amount"
												  v-validate="modelValidations.amount"
								></MoneyAmountInput>
								<span v-if="errors.has('amount')" class="validation-error">{{
										errors.first("amount")
									}}</span>
							</md-field>
							<div class="margin_r"></div>
							<currency-select class="w_33"
								:value="form.currency"
								:isError="!form.currency&&touched"
								v-on:input="selectCurrency"
								:required="true"
								data-vv-name="currency"
								v-validate="modelValidations.currency"
								:error="errors.first('currency')"
							></currency-select>
						</div>
						<div class="flex">
							<DatePicker class="flex_1"
								v-on:input="changeInvoiceDate"
								:value="form.invoiceDate"
								valueFormat="dd/MM/yyyy"
								:placeholder="$t('seller.upload.InvoiceDate')"
								required
								data-vv-name="invoiceDate"
								v-validate="modelValidations.invoiceDate"
								:error="errors.first('invoiceDate')"
							></DatePicker>
							<div class="margin_r"></div>
							<DatePicker class="flex_1"
								v-on:input="changeDueDate"
								:value="form.dueDate"
								valueFormat="dd/MM/yyyy"
								:placeholder="$t('seller.upload.DueDate')"
								required
								data-vv-name="dueDate"
								v-validate="modelValidations.dueDate"
								:error="errors.first('dueDate')"
							></DatePicker>
						</div>
						<CountrySelect
							:labelText="$t('seller.upload.ApplicableLaw')"
							v-model="form.applicableLaw"
							:companyType=enumDef.companyType.DebtorCompany
							:required="true"
							data-vv-name="applicableLaw"
							v-validate="modelValidations.applicableLaw"
							:error="errors.first('applicableLaw')"
							:tooltip-text="$t('seller.upload.applicableLaw.tooltip')"
						></CountrySelect>
						<div class="flex">
							<CommonPdfUpload class="flex_1"
								v-model="form.pod1"
								placeholderKey="POD 1"
								ref="pod1"
								:uploadFunction="uploadProofOfDelivery"
							></CommonPdfUpload>
							<div class="margin_r"></div>
							<CommonPdfUpload class="flex_1"
								v-model="form.pod2"
								placeholderKey="POD 2"
								ref="pod2"
								:uploadFunction="uploadProofOfDelivery"
							></CommonPdfUpload>
						</div>
						<div v-if="!hasPredefinedSaleParameters" class="flex flex_column">
							<md-checkbox v-model="form.shouldBeBundled" @change="bundleRequestChanged">
								{{ $t("seller.upload.bundleRequest") }}
								<el-tooltip placement="top" style="margin-left: 5px">
									<span slot="content" v-html="$t('seller.upload.bundleRequest.tooltip')"></span>
									<i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
								</el-tooltip>
							</md-checkbox>
						</div>
						<h4 class="font_lg font_bold margin_top">{{ $t("seller.upload.salesParameters") }}</h4>
						<AssignmentTypeSelect
							v-model="form.assignmentType"
							:required="true"
							data-vv-name="assignmentType"
							v-validate="modelValidations.assignmentType"
							:error="errors.first('assignmentType')"
						></AssignmentTypeSelect>
						<div v-if="!form.shouldBeBundled && !hasPredefinedSaleParameters">
							<md-radio class="w_100" style="margin-bottom: 0;"
									  v-model="form.sellPriceCalculationType" :value="enumDef.sellPriceCalculationType.None"
							>
								{{ $t("seller.upload.salesParam.none") }}
							</md-radio>
							<UploadSalesParam v-model="form.sellPriceDiscountRate"
											  :selected-type="form.sellPriceCalculationType"
											  :type-value="enumDef.sellPriceCalculationType.Discount"
											  :suggested-price="sellPriceSuggestion.discountRate"
											  :show-unknown-rating-hint="!sellPriceSuggestion.debtorHasKnownRating"
											  @select="selectSellPriceCalculationType"
											  @suggest="suggestPrice"
											  name-translation-key="seller.upload.salesParam.discount"
											  data-vv-name="sellPriceDiscountRate"
											  v-validate="modelValidations.sellPriceDiscountRate"
											  :error="errors.first('sellPriceDiscountRate')"
						    />
							<UploadSalesParam  v-model="form.sellPriceCostOfCapitalRate"
											  :selected-type="form.sellPriceCalculationType"
											  :type-value="enumDef.sellPriceCalculationType.CostOfCapital"
											  :suggested-price="sellPriceSuggestion.costOfCapitalRate"
											   :show-unknown-rating-hint="!sellPriceSuggestion.debtorHasKnownRating"
											  @select="selectSellPriceCalculationType"
											  @suggest="suggestPrice"
											  name-translation-key="seller.upload.salesParam.costOfCapital"
											  :formula-url="costOfCapitalFormulaUrl"
							/>
							<DurrationSelect v-model="form.offerDurationInHours" :no-table="true"/>
						</div>
						<div class="flex text_left margin_top">
							<md-checkbox v-model="form.isRetentionAllowed" :disabled="retentionCheck.isRetentionMandatory" style="margin-top: 5px;"/>
							<span>
								<span v-html="$t(retentionCheck.params.textKey, retentionCheck.params)"></span>
								<el-tooltip placement="top" style="margin-left: 5px">
									<span slot="content" v-html="$t(retentionCheck.params.tooltipKey, retentionCheck.params)"></span>
									<i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
								</el-tooltip>
							</span>
						</div>
					</div>
				</div>
			</div>
			<div class="flex margin_top">
				<div class="flex flex_between flex_1">
					<md-button
						class="md-blank"
						@click="confirmForSave('saveItem')"
					>{{ $t("investor.FilterRight.save") }}
					</md-button>
					<md-button class="md-block"
							   @click="confirmDeletion"
					>{{ $t("common.button.delete") }}
					</md-button>
					<md-button :class="{ 'md-primary': isFormValid }"
							   @click="confirmForSave('saveAndSubmit')"
					>{{ $t("common.button.toReview")}}
					</md-button>
				</div>
			</div>
		</form>
		<UploadConfirmation @confirm="save" ref="confirmation" />
  </div>
</template>
<script>
import AvailableDebtorSelect from "@/components/templates/seller/AvailableDebtorSelect.vue";
import DebtorContactSelect from "src/components/templates/select/DebtorContactSelect.vue";
import CurrencySelect from "src/components/templates/select/CurrencySelect.vue";
import CountrySelect from "src/components/templates/select/CountrySelect.vue";
import DatePicker from "src/components/templates/DatePicker";
import {validateForm, validateRequiredFields} from "src/validation/validation";

import {mapGetters} from "vuex";
import {receivableApi} from "src/common/api/receivableApi";
import MoneyAmountInput from "src/components/templates/common/MoneyAmountInput";

import TypeSelect from "src/components/templates/select/TypeSelect.vue";
import {enumDef} from "src/common/enumDefs";
import CommonPdfUpload from "@/components/templates/common/CommonPdfUpload.vue";
import AssignmentTypeSelect from "@/components/templates/select/AssignmentTypeSelect.vue";
import UploadConfirmation from "@/components/templates/seller/UploadConfirmation.vue";
import {formatAsMoneyAmount, formatAsPercentage} from "@/common/format";
import {retentionCheckApi} from "@/common/api/retentionCheckApi";
import UploadSalesParam from "@/components/templates/seller/UploadSalesParam.vue";
import DurrationSelect from "@/components/templates/seller/sell/DurrationSelect.vue";
import {sellPriceFormula} from "@/components/templates/seller/sell/sellPriceFormula";

export default {
  props: {
		getDetail: Function
	},
	components: {
		DurrationSelect,
		UploadSalesParam,
		UploadConfirmation,
    	AssignmentTypeSelect,
		MoneyAmountInput,
		AvailableDebtorSelect,
		DebtorContactSelect,
		CurrencySelect,
		CountrySelect,
		DatePicker,
		TypeSelect,
    	CommonPdfUpload,
	},
	data() {
		let defaultForm = {
			invoiceNumber: "",
			invoiceDate: "",
			dueDate: "",
			amount: "",
			currency: "",
			applicableLaw: "",
			requestObligorConfirmation: false,
			shouldBeBundled: false,
			isRetentionAllowed: false,
			type: enumDef.receivableType.SingleInvoice,
			assignmentType: null,
			sellPriceDiscountRate: null,
			sellPriceCostOfCapitalRate: null,
			sellPriceCalculationType: enumDef.sellPriceCalculationType.None,
			offerDurationInHours: null,
			pod1: null,
			pod2: null,
		};

		let defaultSellPriceSuggestion = {
			discountRate: null,
			costOfCapitalRate: null,
			debtorHasKnownRating: false,
		};

		return {
			defaultForm: { ...defaultForm },
			form: { ...defaultForm },
			initialForm: { ...defaultForm },
			formChanged: false,
			debtorChanged: false,
			debtorInitiallySet: false,
			contactChanged: false,
			contactInitiallySet: false,
			readOnly: false,
			modelValidations: {
				debtorData: {
					required: true
				},
				contactData: {
					required: true
				},
				amount: {
					required: true
				},
				currency: {
					required: true
				},
				invoiceNumber: {
					required: true
				},
				invoiceDate: {
					required: true
				},
				dueDate: {
					required: true
				},
				applicableLaw: {
					required: true
				},
				assignmentType: {
					required: true
				},
				sellPriceDiscountRate: {
					required_if_func: { func: (_) => this.form.sellPriceCalculationType === enumDef.sellPriceCalculationType.Discount }
				},
				sellPriceCostOfCapitalRate: {
					required_if_func: { func: (_) => this.form.sellPriceCalculationType === enumDef.sellPriceCalculationType.CostOfCapital }
				},
			},
			touched: false,
			saveType: "",
			saveData: null,
			defaultSellPriceSuggestion: defaultSellPriceSuggestion,
			sellPriceSuggestion: { ...defaultSellPriceSuggestion },
			inheritedPrefs: null,
			retentionCheck: {
				isRetentionMandatory: false,
				params: {
					currency: "EUR",
					textKey: null,
					tooltipKey: null,
					url: null,
					retentionAmountThreshold: null,
					retentionInPercent: null
				}
			},
			enumDef: enumDef,
		};
	},
	created() {
		receivableApi.inspection.getInheritedPartnerPrefs()
			.then(res => this.inheritedPrefs = res)
			.catch(this.toast);
	},
	methods: {
		loadAvailableDebtors() {
			return receivableApi.availableDebtors();
		},
		updateRetentionCheck(currency) {
			retentionCheckApi.checkRetention({ amount: Number.MAX_VALUE, currency: currency })
				.then(res => {
					this.retentionCheck = {
						...res,
						params: {
							currency: currency,
							textKey: res.isRetentionMandatory ? "seller.upload.retention.mandatory.check" : "seller.upload.retention.optional.check",
							tooltipKey: res.isRetentionMandatory ? "seller.upload.retention.mandatory.description" : "seller.upload.retention.optional.description",
							url: res.termsAndConditionsUrl,
							retentionAmountThreshold: formatAsMoneyAmount(res.retentionAmountThreshold, { currency: currency, decimals: 0 }),
							retentionInPercent: formatAsPercentage(res.retentionRate, { decimals: 0 })
						}
					};

					// Enforce retention allowed if it is mandatory
					if (!this.form.isRetentionAllowed && res.isRetentionMandatory) {
						this.form.isRetentionAllowed = true;
					}
				})
				.catch(this.toast);
		},
		suggestPrice() {
			validateForm(this, this.$validator, ["sellPriceDiscountRate", "sellPriceCostOfCapitalRate"])
				.then(() => {
					receivableApi.inspection.suggestPricing(this.buildReceivableData())
						.then(res => this.sellPriceSuggestion = res)
						.catch(this.toast);
				})
				.catch(this.toast);
		},
		selectAssignmentType(e) {
			this.form.assignmentType = e;
		},

		selectType(e) {
			this.form.type = e;
		},
		changeDebtor(data) {
			if (!this.debtorInitiallySet) {
				this.debtorInitiallySet = true;
			} else {
				this.debtorChanged = true;
				this.updatePdfChangeStatus();
			}

			// Only set applicable law if it is saved in the debtor relation
			if (!this.form.applicableLaw && data.applicableLaw) {
				this.form.applicableLaw = data.applicableLaw;
			}

			this.$store.dispatch("setActionDebtorData", data);

			if (this.debtorChanged) {
				this.resetSalesParams();

				if (this.contactData) {
					this.changeContact(null);
				}
			}

			this.autoAdjustDueDate()
		},
		changeContact(data) {
			if (!this.contactInitiallySet) {
				this.contactInitiallySet = true;
			} else {
				this.contactChanged = true;
				this.updatePdfChangeStatus();
			}

			this.$store.dispatch("setActionContactData", data);
		},
		changeInvoiceDate(e) {
			if (this.form.invoiceDate === e) {
				return;
			}

			this.form.invoiceDate = e;
      this.autoAdjustDueDate();
	  this.resetSalesParams();
		},
		changeDueDate(e) {
			if (this.form.dueDate === e) {
				return;
			}

			this.form.dueDate = e;
			this.resetSalesParams();
		},
    autoAdjustDueDate() {
		  if (!this.debtorData) {
		    return;
      }
      if (!this.form.dueDate && this.debtorData.historicalPaymentDays && this.form.invoiceDate) {
        let automaticDueDate = moment(this.form.invoiceDate, "DD/MM/YYYY")
            .add(this.debtorData.historicalPaymentDays, 'd')
            .format("DD/MM/YYYY");

        this.changeDueDate(automaticDueDate);
      }
    },
		selectCurrency(currency) {
			if (this.form.currency === currency) {
				return;
			}

			this.form.currency = currency;
			this.updateRetentionCheck(currency);
			this.resetSalesParams();
		},
		bundleRequestChanged() {
			if (this.form.shouldBeBundled) {
				this.selectSellPriceCalculationType(enumDef.sellPriceCalculationType.None);
			}
		},
		selectSellPriceCalculationType(type) {
			this.form.sellPriceCalculationType = type;

			/*this.modelValidations.sellPriceDiscountRate.required = type === enumDef.sellPriceCalculationType.Discount;
			this.modelValidations.sellPriceCostOfCapitalRate.required = type === enumDef.sellPriceCalculationType.CostOfCapital;*/
		},
		uploadProofOfDelivery(params) {
		  return receivableApi.uploadProofOfDelivery( { file: params.file });
		},
		removeItem(id, invoiceNumber, type) {
			let pList = this.pafList;
			const indexToDelete = this.pafList.findIndex(n => n.receivableId === id);
			let nextItem;
			if (indexToDelete !== -1) {
				if (type === "saveAndSubmit") {
					pList.splice(indexToDelete, 1);
				} else {
					pList[indexToDelete].invoiceNumber = invoiceNumber;
				}
			}
			if (pList.length === 0) {
				this.$store.dispatch("setActionPdfDialog", { show: false });
				return;
			} else {
				if (type === "saveAndSubmit") {
					nextItem = pList[indexToDelete];
				} else {
					nextItem = pList[indexToDelete + 1];
				}
				if (nextItem) {
					this.$store.dispatch("setActionActivePdf", nextItem);
					this.getDetail(nextItem.receivableId);
				}
			}
			this.$store.dispatch("setActionPdfList", pList);
		},
		confirmForSave(type) {
			validateForm(this, this.$validator)
			.then(() => {
				if (type === "saveAndSubmit") {
					this.saveType = type;
				  	this.saveData = this.buildReceivableData();

					this.showConfirmation();
				} else {
				  this.saveType = type;
				  this.saveData = this.buildReceivableData();

				  this.save();
				}
			})
			.catch(e => this.toast(e));
		},
		showConfirmation() {
			this.$refs.confirmation.showWithReceivable(this.saveData);
		},
		save() {
			let type = this.saveType;
			this.touched = true;

			receivableApi[type](this.saveData)
				.then(res => {
					this.toastSuccess();
					this.touched = false;
					this.removeItem(this.form.id, res.invoiceNumber, type);
				})
				.catch(err => {
					this.touched = false;
					this.toast(err);
				});
		},
		buildReceivableData() {
			let option = {...this.form };

			let { requestObligorConfirmation, amount, assignmentType} = this.form;
			let { invoiceDocumentFileId, invoiceDocumentFilename } = this.activePdf;

			option.invoiceDocumentFilename = invoiceDocumentFilename;
			option.invoiceDocument = invoiceDocumentFileId;
			option.debtor = { id: this.debtorId };
			option.debtorContact = this.contactData;
			option.requestObligorConfirmation = requestObligorConfirmation;
			option.amount = amount;
			option.disclosed = assignmentType === 1;
			option.pod1FileId = this.form.pod1?.fileId;
			option.pod2FileId = this.form.pod2?.fileId;

			if (this.form.sellPriceCalculationType === enumDef.sellPriceCalculationType.Discount) {
				option.sellPriceCalculationRate = this.form.sellPriceDiscountRate;
			} else if (this.form.sellPriceCalculationType === enumDef.sellPriceCalculationType.CostOfCapital) {
				option.sellPriceCalculationRate = this.form.sellPriceCostOfCapitalRate;
			} else {
				option.sellPriceCalculationType = enumDef.sellPriceCalculationType.None;
				option.sellPriceCalculationRate = null;
			}

			return option;
		},
		confirmDeletion() {
			let option = {
				title: "",
				msg: this.$t("UpLoadTable.del.tip"),
				confirmFun: this.delItem,
				params: this.form.id
			};
			this.confirmMeg(option);
		},
		delItem(id) {
			let option = [id];
			this.touched = true;
			receivableApi
			.deleteOperation(option)
			.then(res => {
				this.touched = false;
				this.removeItem(id, res.invoiceNumber, "saveAndSubmit");
			})
			.catch(err => {
				this.touched = false;
				this.toast(err);
			});
		},
		didFormChange() {
			return this.didChange(this.form, this.initialForm);
		},
		didChange(data, initial) {
			for (let key in initial) {
				if (initial[key] !== data[key]) {
					return true;
				}
			}

			return false;
		},
		updatePdfChangeStatus() {
			this.activePdf.changed = this.formChanged || this.debtorChanged || this.contactChanged;
		},
		resetSalesParams() {
			this.sellPriceSuggestion = { ...this.defaultSellPriceSuggestion };
			this.form.sellPriceDiscountRate = null;
			this.form.sellPriceCostOfCapitalRate = null;
		},
		beforeRedirect() {
			this.$store.dispatch("setActionPdfDialog", { show: false });
		},
		updateSaleParameters() {
			// Assignment type
			this.form.assignmentType = this.tableItemDetail.isFromInitialization
				// check if partner settings exist for auction disclosure
				? (this.tableItemDetail.isDisclosedFromPartnerSettings !== undefined
						// check if partner settings are enabled for auction disclosure
						? (this.tableItemDetail.isDisclosedFromPartnerSettings === true ? 1 : 0)
						// no preselection for auction disclosure if not specified in partner settings
						: null
				)
				: (this.tableItemDetail.disclosed ? 1 : 0);

			// Only set pricing parameters if they are supposed to be given by the user.
			// Predefined parameters by partners are not shown and should therefore not be set in the data as well
			if (this.tableItemDetail.saleParameters.source === enumDef.sellPriceSource.User) {
				// Setup only the selected pricing parameter
				let calculationType = this.tableItemDetail.saleParameters.calculationType;
				this.form.sellPriceCalculationType = calculationType;

				if (calculationType === enumDef.sellPriceCalculationType.Discount) {
					this.form.sellPriceDiscountRate = this.tableItemDetail.saleParameters.discountRate;
				} else if (calculationType === enumDef.sellPriceCalculationType.CostOfCapital) {
					this.form.sellPriceCostOfCapitalRate = this.tableItemDetail.saleParameters.costOfCapitalRate;
				}
			} else {
				this.form.sellPriceCalculationType = enumDef.sellPriceCalculationType.None;
				this.form.sellPriceDiscountRate = null;
				this.form.sellPriceCostOfCapitalRate = null;
			}

			this.form.offerDurationInHours = this.tableItemDetail.saleParameters.offerDurationInHours || enumDef.SellDurationOptions.properties[enumDef.SellDurationOptions.default].value;
			this.form.shouldBeBundled = this.tableItemDetail.saleParameters.shouldBeBundled;
			this.form.isRetentionAllowed = this.tableItemDetail.saleParameters.retentionAllowed;
		}
	},
	computed: {
		...mapGetters({
			debtorData: "debtorData",
			contactData: "contactData",
			tableItemData: "tableItemData",
			tableItemDetail: "tableItemDetail",
			pafList: "pafList",
			activePdf: "activePdf"
		}),
		isFormValid() {
			return validateRequiredFields(this.form, this.modelValidations);
		},
		debtorId() {
			return this.debtorData ? this.debtorData.id : null;
		},
		hasPredefinedSaleParameters() {
			return this.inheritedPrefs && this.inheritedPrefs.calculatePriceAtUpload;
		},
		costOfCapitalFormulaUrl() {
			return sellPriceFormula.fileUrlForCurrentLocale();
		},
	},
	watch: {
		tableItemDetail() {
			this.$validator.reset();
			this.formChanged = false;
			this.debtorChanged = false;
			this.debtorInitiallySet = false;
			this.contactChanged = false;
			this.contactInitiallySet = false;

			this.form = {
				...this.defaultForm,
				...this.tableItemDetail
			};
			this.initialForm = { ...this.form };

			let { requestObligorConfirmation } = this.form;
			if (this.form.invoiceDate) {
				this.readOnly = true;
			}
			this.form.requestObligorConfirmation = requestObligorConfirmation;

			if (this.tableItemDetail) {
				// use applicable law from receivable if possible
				this.form.applicableLaw = this.tableItemDetail.applicableLaw;

				// Fallback to the applicable law of the debtor relation
				if (!this.form.applicableLaw && this.tableItemDetail.debtorRelationData) {
					this.form.applicableLaw = this.tableItemDetail.debtorRelationData.applicableLaw;
				}

				if (this.tableItemDetail.proofsOfDelivery && this.tableItemDetail.proofsOfDelivery.length > 0) {
					this.tableItemDetail.proofsOfDelivery.forEach((pod, index) => {
						this.$refs["pod" + index].changeFileData(pod);
					})
				}

				this.updateSaleParameters();

				if (this.isFormValid && this.form.sellPriceCalculationType !== enumDef.sellPriceCalculationType.None) {
					this.suggestPrice();
				}

				this.updateRetentionCheck(this.form.currency || "EUR");
			}

      this.autoAdjustDueDate()
		},
		form: {
			deep: true,
			handler() {
				this.formChanged = this.didFormChange();
				this.updatePdfChangeStatus();
			}
		}
	}
};
</script>